var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c, _d, _e;
import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { BottomDecibels, DefaultMaxDecibels, DefaultMinDecibels, TopDecibels, } from "./constants.js";
import { DefaultCubeHelix, } from "./palette.js";
import { SpectrumDecibelRangeChangedEvent } from "./events.js";
import { DragController } from "../controls/drag-controller.js";
let RrDecibelRange = class RrDecibelRange extends LitElement {
    constructor() {
        super(...arguments);
        /** Lower value of the range. */
        this.minDecibels = DefaultMinDecibels;
        /** Higher value of the range. */
        this.maxDecibels = DefaultMaxDecibels;
        /** Palette used in the waterfall. */
        this.palette = DefaultCubeHelix;
    }
    static get styles() {
        return [
            css `
        :host {
          position: relative;
          display: flex;
          flex-direction: row;
          align-items: stretch;
        }

        #palette {
          flex: 1;
          height: 24px;
          min-width: ${1.25 * (TopDecibels - BottomDecibels)}px;
        }

        @media (max-width: 375px) {
          #palette {
            min-width: ${TopDecibels - BottomDecibels}px;
          }
        }

        #min,
        #max {
          width: 7ex;
          align-content: center;
        }

        #min {
          text-align: right;
          padding-right: 8px;
        }

        #max {
          text-align: left;
          padding-left: 8px;
        }

        #minThumb,
        #maxThumb {
          position: absolute;
          cursor: ew-resize;
          box-sizing: border-box;
          width: 8px;
          height: 24px;
          background: lightgray;
          border: 1px outset;
        }

        #minThumb {
          border-radius: 4px 0 0 4px;
        }

        #maxThumb {
          border-radius: 0 4px 4px 0;
        }

        .touchArea {
          position: absolute;
          top: -5px;
          bottom: -5px;
          left: -15px;
          right: -15px;
        }
      `,
        ];
    }
    render() {
        return html ` <input
        id="min"
        type="text"
        .value=${getDisplayDbValue(this.minDecibels)}
        @focus=${this.onMinFocus}
        @blur=${this.onMinBlur}
        @change=${this.onMinChange}
      />
      <canvas id="palette" width="256" height="24"></canvas>
      <input
        id="max"
        type="text"
        .value=${getDisplayDbValue(this.maxDecibels)}
        @focus=${this.onMaxFocus}
        @blur=${this.onMaxBlur}
        @change=${this.onMaxChange}
      />
      <div id="minThumb" @pointerdown=${this.onMinPointerDown}>
        <div class="touchArea"></div>
      </div>
      <div id="maxThumb" @pointerdown=${this.onMaxPointerDown}>
        <div class="touchArea"></div>
      </div>`;
    }
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.minDragController = new DragController(new DecibelDragHandler("min", this, this.paletteBox), 0);
        this.maxDragController = new DragController(new DecibelDragHandler("max", this, this.paletteBox), 0);
        const resizeObserver = new ResizeObserver(() => this.repaintPalette());
        resizeObserver.observe(document.body);
        this.repaintPalette();
    }
    updated(changed) {
        super.updated(changed);
        this.repaintPalette();
    }
    repaintPalette() {
        let ctx = this.getContext();
        if (!ctx)
            return;
        for (let x = 0; x < ctx.canvas.width; ++x) {
            let db = (x * (TopDecibels - BottomDecibels)) / 255 + BottomDecibels;
            let color = (255 * (db - this.minDecibels)) / (this.maxDecibels - this.minDecibels);
            if (color < 0)
                color = 0;
            if (color > 255)
                color = 255;
            color = Math.floor(color);
            ctx.fillStyle = paletteColor(this.palette[color]);
            ctx.fillRect(x, 0, 1, 24);
        }
        if (this.minBox) {
            this.minBox.style.backgroundColor = paletteColor(this.palette[0]);
            this.minBox.style.color = isDark(this.palette[0]) ? "white" : "black";
        }
        if (this.maxBox) {
            this.maxBox.style.backgroundColor = paletteColor(this.palette[255]);
            this.maxBox.style.color = isDark(this.palette[255]) ? "white" : "black";
        }
        if (this.minThumb && this.paletteBox) {
            this.minThumb.style.right =
                ((this.minDecibels - TopDecibels) * this.paletteBox.offsetWidth) /
                    (BottomDecibels - TopDecibels) +
                    this.paletteBox.offsetLeft +
                    "px";
        }
        if (this.maxThumb && this.paletteBox) {
            this.maxThumb.style.left =
                ((this.maxDecibels - BottomDecibels) * this.paletteBox.offsetWidth) /
                    (TopDecibels - BottomDecibels) +
                    this.paletteBox.offsetLeft +
                    "px";
        }
    }
    getContext() {
        if (this.context)
            return this.context;
        if (!this.paletteBox)
            return;
        this.context = this.paletteBox.getContext("2d");
        return this.context;
    }
    onMinFocus(e) {
        let target = e.target;
        target.value = getInputDbValue(this.minDecibels);
    }
    onMinBlur(e) {
        let target = e.target;
        target.value = getDisplayDbValue(this.minDecibels);
    }
    onMinChange(e) {
        let target = e.target;
        let value = target.value;
        if (value.endsWith("dB"))
            value = value.substring(0, value.length - 2).trim();
        let input = Number(value);
        if (isNaN(input)) {
            target.value = getDisplayDbValue(this.minDecibels);
        }
        else {
            setMinDb(input, this);
        }
    }
    onMaxFocus(e) {
        let target = e.target;
        target.value = getInputDbValue(this.maxDecibels);
    }
    onMaxBlur(e) {
        let target = e.target;
        target.value = getDisplayDbValue(this.maxDecibels);
    }
    onMaxChange(e) {
        let target = e.target;
        let value = target.value;
        if (value.endsWith("dB"))
            value = value.substring(0, value.length - 2).trim();
        let input = Number(value);
        if (isNaN(input)) {
            target.value = getDisplayDbValue(this.maxDecibels);
        }
        else {
            setMaxDb(input, this);
        }
    }
    onMinPointerDown(e) {
        this.minDragController?.startDragging(e);
    }
    onMaxPointerDown(e) {
        this.maxDragController?.startDragging(e);
    }
};
__decorate([
    property({ type: Number, reflect: true, attribute: "min-decibels" }),
    __metadata("design:type", Number)
], RrDecibelRange.prototype, "minDecibels", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: "max-decibels" }),
    __metadata("design:type", Number)
], RrDecibelRange.prototype, "maxDecibels", void 0);
__decorate([
    property({ attribute: false }),
    __metadata("design:type", Object)
], RrDecibelRange.prototype, "palette", void 0);
__decorate([
    query("#min"),
    __metadata("design:type", typeof (_a = typeof HTMLElement !== "undefined" && HTMLElement) === "function" ? _a : Object)
], RrDecibelRange.prototype, "minBox", void 0);
__decorate([
    query("#max"),
    __metadata("design:type", typeof (_b = typeof HTMLElement !== "undefined" && HTMLElement) === "function" ? _b : Object)
], RrDecibelRange.prototype, "maxBox", void 0);
__decorate([
    query("#palette"),
    __metadata("design:type", typeof (_c = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _c : Object)
], RrDecibelRange.prototype, "paletteBox", void 0);
__decorate([
    query("#minThumb"),
    __metadata("design:type", typeof (_d = typeof HTMLElement !== "undefined" && HTMLElement) === "function" ? _d : Object)
], RrDecibelRange.prototype, "minThumb", void 0);
__decorate([
    query("#maxThumb"),
    __metadata("design:type", typeof (_e = typeof HTMLElement !== "undefined" && HTMLElement) === "function" ? _e : Object)
], RrDecibelRange.prototype, "maxThumb", void 0);
RrDecibelRange = __decorate([
    customElement("rr-decibel-range")
], RrDecibelRange);
export { RrDecibelRange };
function paletteColor(entry) {
    return `rgb(${entry[0]}, ${entry[1]}, ${entry[2]})`;
}
function isDark(entry) {
    return Math.max(entry[0], entry[1], entry[2]) < 96;
}
class DecibelDragHandler {
    constructor(type, range, box) {
        this.type = type;
        this.range = range;
        this.box = box;
        this.startDb = 0;
    }
    startDrag() {
        this.startDb =
            this.type === "min" ? this.range.minDecibels : this.range.maxDecibels;
    }
    drag(deltaX, _) {
        let fraction = deltaX / this.box.offsetWidth;
        this.changeDb(this.startDb + fraction * (TopDecibels - BottomDecibels));
    }
    finishDrag() { }
    cancelDrag() {
        this.changeDb(this.startDb);
    }
    onClick() { }
    changeDb(db) {
        if (this.type == "min") {
            setMinDb(db, this.range);
        }
        else {
            setMaxDb(db, this.range);
        }
    }
}
function setMinDb(db, range) {
    db = Math.round(db);
    if (db < BottomDecibels)
        db = BottomDecibels;
    if (db > TopDecibels)
        db = TopDecibels;
    if (db > range.maxDecibels - 6)
        db = range.maxDecibels - 6;
    range.minDecibels = db;
    range.dispatchEvent(new SpectrumDecibelRangeChangedEvent({ min: db }));
}
function setMaxDb(db, range) {
    db = Math.round(db);
    if (db < BottomDecibels)
        db = BottomDecibels;
    if (db > TopDecibels)
        db = TopDecibels;
    if (db < range.minDecibels + 6)
        db = range.minDecibels + 6;
    range.maxDecibels = db;
    range.dispatchEvent(new SpectrumDecibelRangeChangedEvent({ max: db }));
}
function getDisplayDbValue(value) {
    return getInputDbValue(value) + " dB";
}
function getInputDbValue(value) {
    return String(value);
}
